import { useEffect, useSyncExternalStore, type ReactNode } from "react";

let cached: boolean = false;

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return cached;
}

function getServerSnapshot(): boolean {
  return false;
}

function writePresentMode(next: boolean): void {
  if (cached === next) return;
  cached = next;
  listeners.forEach((l) => l());
}

function togglePresentModeStore(): void {
  writePresentMode(!cached);
}

/**
 * Present mode is an in-app toggle for the live customer walkthrough.
 * When the energy advisor is sitting with a customer (in person or on a
 * screenshare), flipping this on strips the app chrome (sidebar, top
 * nav) so the customer sees the same surface the deprecated shared
 * link used to render — same SnapshotHeader, same tabs, same RFQ
 * walkthrough, just without the energy advisor's workspace envelope. Can
 * also be toggled with `Shift+P` anywhere in the Energy Advisor workspace.
 *
 * State is intentionally not persisted — any full-page reload (logout,
 * session expiry, plain refresh) resets it. The trade-off is that an
 * accidental refresh during a meeting drops out of present mode, but
 * the alternative (sessionStorage persistence) lets the flag survive
 * across a logout/login boundary and re-drop the next session into
 * present mode without the user asking for it.
 *
 * State lives in a module-scoped external store rather than React
 * context so `AppShell` (which sits above the Energy Advisor subtree) can read
 * it and switch envelopes without the Energy Advisor routes having to mount
 * their own layout.
 */
export function usePresentMode(): {
  presentMode: boolean;
  setPresentMode: (next: boolean) => void;
  toggle: () => void;
} {
  const presentMode = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );
  return {
    presentMode,
    setPresentMode: writePresentMode,
    toggle: togglePresentModeStore,
  };
}

// ── Scoped account count (UAT #10) ───────────────────────────────────────────
//
// The customer header's "N accounts" defaults to the CRM `account_count`, which
// is 0 until CRM sync populates it — and is unrelated to what the Bill Analyzer
// actually loaded. When an analyzer report is open, the header should show the
// analyzer's own `rollup.account_count`. The header renders ABOVE the tab
// Outlet, so the tab can't prop it up; a module-scoped store bridges it, the
// same shape as present mode. `null` = no override (fall back to CRM).

let scopedAccountCount: number | null = null;
const accountListeners = new Set<() => void>();

function subscribeAccountCount(listener: () => void): () => void {
  accountListeners.add(listener);
  return () => {
    accountListeners.delete(listener);
  };
}

function getAccountCountSnapshot(): number | null {
  return scopedAccountCount;
}

function getAccountCountServerSnapshot(): number | null {
  return null;
}

/** Read the scoped account-count override for the header. `null` = none. */
export function useScopedAccountCount(): number | null {
  return useSyncExternalStore(
    subscribeAccountCount,
    getAccountCountSnapshot,
    getAccountCountServerSnapshot,
  );
}

/** Set (or clear, with `null`) the header's account-count override. The Bill
 *  Analyzer tab calls this with its `rollup.account_count` while a report is
 *  open and clears it on unmount, so the count follows the open analysis and
 *  reverts to the CRM total elsewhere. */
export function setScopedAccountCount(next: number | null): void {
  if (scopedAccountCount === next) return;
  scopedAccountCount = next;
  accountListeners.forEach((l) => l());
}

interface EnergyAdvisorPresentationProviderProps {
  children: ReactNode;
}

/**
 * Wraps the Energy Advisor subtree to bind the `Shift+P` keyboard shortcut.
 * The toggle state itself lives in the external store consumed by
 * `usePresentMode()`; this component only scopes the keyboard handler
 * so the shortcut is active inside `/energy-advisor/*` without leaking across
 * the rest of the app.
 */
export function EnergyAdvisorPresentationProvider({
  children,
}: EnergyAdvisorPresentationProviderProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "P" || !e.shiftKey) return;
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        active?.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
      togglePresentModeStore();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return <>{children}</>;
}
